/**
 * /app/pos — POS: hub de punto de venta.
 * Apertura de caja con validación de PIN (si está configurado).
 * Gateado por payment_pos_mode.
 */
import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Store, DollarSign, Clock, FileText, Plus, X, KeyRound, ShieldCheck } from "lucide-react";
import { CrudEmptyState, CrudShellHeader, DashboardCard, Loading, Panel } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import {
  formatMoneyInputEsAr,
  parseMoneyInputEsAr,
  reformatMoneyInputEsArWhileTyping,
} from "@/lib/money-input-es-ar";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useLocationContext } from "@/lib/location-context";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

function PosRoot() {
  const matchRoute = useMatchRoute();
  const isOnChild =
    Boolean(matchRoute({ to: "/app/pos/caja", fuzzy: true })) ||
    Boolean(matchRoute({ to: "/app/pos/movimientos", fuzzy: true })) ||
    Boolean(matchRoute({ to: "/app/pos/reporte", fuzzy: true }));
  if (isOnChild) return <Outlet />;
  return <PosPage />;
}

export const Route = createFileRoute("/app/pos")({
  component: PosRoot,
  head: () => ({ meta: [{ title: "POS — Structura Market" }] }),
});

interface CashSession {
  id: string;
  status: "open" | "closed";
  openedAt: string;
  initialAmount: number;
  openedBy: string;
}

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

const PIN_LENGTH = 4;

function PinInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col items-center gap-3">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <KeyRound className="h-3.5 w-3.5" /> PIN de cajero (4 dígitos)
      </label>
      <div className="flex gap-3" onClick={() => inputRef.current?.focus()}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={`w-10 h-12 rounded-lg border flex items-center justify-center text-xl font-bold tabular-nums cursor-text transition-colors
              ${i < value.length ? "border-primary bg-primary/10 text-foreground" : "border-border/60 bg-card text-transparent"}`}
          >
            {i < value.length ? "•" : ""}
          </div>
        ))}
      </div>
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={PIN_LENGTH}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH))}
        className="sr-only"
        autoFocus
      />
      <p className="text-[11px] text-muted-foreground">Tocá los círculos para escribir el PIN</p>
    </div>
  );
}

function PosPage() {
  const tid = useWorkspaceTenantId();
  const { activeLocationId, locations } = useLocationContext();
  const resolvedLocationId =
    activeLocationId ?? locations.find((l) => l.isDefault)?.id ?? locations[0]?.id ?? null;
  const [session, setSession] = useState<CashSession | null>(null);
  const [requiresPin, setRequiresPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [opening, setOpening] = useState(false);
  const [showOpen, setShowOpen] = useState(false);

  // Apertura form
  const [initialAmount, setInitialAmount] = useState("");
  /** SCRUM-366: lo que el sistema espera encontrar en caja, según el cierre anterior. */
  const [expectedOpeningAmount, setExpectedOpeningAmount] = useState(0);
  const [pin, setPin] = useState("");
  const [step, setStep] = useState<"amount" | "pin">("amount");

  useEffect(() => {
    if (!tid) return;
    setLoading(true);
    const qs = resolvedLocationId
      ? `?locationId=${encodeURIComponent(resolvedLocationId)}`
      : "";
    apiGetJson(`/api/pos/session${qs}`, tid)
      .then((r: any) => {
        const typed = r as { session?: CashSession; requiresPin?: boolean; expectedOpeningAmount?: number };
        setSession(typed.session ?? null);
        setRequiresPin(typed.requiresPin ?? false);
        setExpectedOpeningAmount(typed.expectedOpeningAmount ?? 0);
      })
      .catch((e: Error & { planLock?: PlanApiErrorPayload }) => {
        // Bug real: apiGetJson lanza (no resuelve) en respuestas no-2xx, así
        // que un `_lock` en el body del 200 nunca se veía — payment_pos_mode
        // sin plan devuelve 402 y el error thrown trae `.planLock` (mismo
        // contrato que el resto de la app, ver app.campaigns.tsx et al.).
        if (e.planLock) { setLock(e.planLock); return; }
        toast.error("Error al cargar sesión POS");
      })
      .finally(() => setLoading(false));
  }, [tid, resolvedLocationId]);

  const handleOpenSession = async () => {
    if (!tid) return;
    if (requiresPin && pin.length < PIN_LENGTH) {
      toast.error("Ingresá el PIN completo (4 dígitos)");
      return;
    }
    setOpening(true);
    try {
      const r = (await apiSendJson(
        `/api/pos/session`,
        "POST",
        { initialAmount: (() => {
          const n = parseMoneyInputEsAr(initialAmount);
          return Number.isFinite(n) ? n : 0;
        })(), pin: pin || undefined, locationId: resolvedLocationId },
        tid,
      )) as { session?: CashSession; error?: string };
      if (r.error) {
        toast.error(r.error);
        setPin("");
        setStep("amount");
        return;
      }
      setSession(r.session!);
      setShowOpen(false);
      setPin("");
      setStep("amount");
      toast.success("Sesión de caja abierta");
    } catch {
      toast.error("Error al abrir sesión");
    } finally {
      setOpening(false);
    }
  };

  const handleStartOpen = () => {
    setShowOpen(true);
    setStep(requiresPin ? "amount" : "amount");
    setPin("");
    setInitialAmount("");
  };

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Punto de Venta" description="Sesión de caja y cobros en mostrador" />
        <Loading variant="list" rows={2} />
      </div>
    );
  }
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <CrudShellHeader title="Punto de Venta" description="Sesión de caja y cobros en mostrador" />
      <ClinicalPageShell offset="none">
        <MerchantExperienceBar screenId="pos" />
        {!session ? (
        <div className="flex flex-col items-center gap-6 py-8">
          {!showOpen ? (
            <>
              <CrudEmptyState
                icon={<Store className="h-8 w-8" />}
                title="No hay sesión de caja abierta"
                description="Abrí una sesión para empezar a cobrar en mostrador"
                className="w-full max-w-sm"
                action={
                  <Button onClick={handleStartOpen}>
                    <Plus className="mr-2 h-4 w-4" /> Abrir sesión
                  </Button>
                }
              />
              {/* SCRUM-368: con la caja cerrada, igual se puede consultar el historial. */}
              <div className="w-full max-w-sm">
                <DashboardCard
                  to="/app/pos/reporte"
                  icon={FileText}
                  title="Sesiones anteriores"
                  subtitle="Consultar cierres de caja previos"
                />
              </div>
            </>
          ) : (
            <Panel title="Abrir sesión de caja" className="w-full max-w-sm">
              <div className="space-y-5 text-center">
                {/* Paso 1: monto inicial */}
                {step === "amount" && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="text-left w-full">
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Efectivo inicial en caja</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={initialAmount}
                        onChange={(e) =>
                          setInitialAmount(reformatMoneyInputEsArWhileTyping(e.target.value))
                        }
                        onBlur={() => {
                          const n = parseMoneyInputEsAr(initialAmount);
                          if (Number.isFinite(n) && n >= 0) {
                            setInitialAmount(formatMoneyInputEsAr(n, { maximumFractionDigits: 2 }));
                          }
                        }}
                        placeholder="0"
                        autoFocus
                        aria-label="Efectivo inicial en caja"
                        className="mt-1.5 w-full rounded-lg border border-border/60 bg-card px-3 py-2.5 text-lg text-right tabular-nums font-mono"
                      />
                    </div>
                    {/* SCRUM-366: referencia read-only según lo que quedó en la sesión anterior */}
                    <div className="flex w-full items-center justify-between rounded-lg border border-border/40 bg-muted/20 px-3 py-2 text-left">
                      <span className="text-xs text-muted-foreground">Efectivo esperado en caja</span>
                      <span className="text-sm font-medium tabular-nums" data-testid="pos-expected-opening-amount">
                        {pesos(expectedOpeningAmount)}
                      </span>
                    </div>
                    <Button
                      className="w-full"
                      onClick={() => requiresPin ? setStep("pin") : handleOpenSession()}
                      disabled={opening}
                    >
                      {requiresPin ? "Siguiente — Ingresar PIN" : (opening ? "Abriendo..." : "Confirmar apertura")}
                    </Button>
                  </div>
                )}

                {/* Paso 2: PIN (solo si requiresPin) */}
                {step === "pin" && requiresPin && (
                  <div className="flex flex-col items-center gap-4">
                    <PinInput value={pin} onChange={setPin} />
                    <div className="flex gap-2 w-full">
                      <Button variant="ghost" size="sm" onClick={() => setStep("amount")} className="flex-1">
                        Atrás
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={handleOpenSession}
                        disabled={opening || pin.length < PIN_LENGTH}
                      >
                        <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                        {opening ? "Validando..." : "Abrir caja"}
                      </Button>
                    </div>
                  </div>
                )}

                <Button size="sm" variant="ghost" className="w-full" onClick={() => { setShowOpen(false); setPin(""); setStep("amount"); }}>
                  <X className="mr-1.5 h-3.5 w-3.5" /> Cancelar
                </Button>
              </div>
            </Panel>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <Panel>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10 text-green-400">
                  <Store className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium">Sesión activa</p>
                  <p className="text-xs text-muted-foreground">
                    Abierta{" "}
                    {new Date(session.openedAt).toLocaleTimeString("es-AR", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                    {" · "}Caja inicial: {pesos(session.initialAmount)}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-green-400">
                Abierta
              </Badge>
            </div>
          </Panel>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <DashboardCard
              to="/app/pos/caja"
              icon={DollarSign}
              title="Cobrar"
              subtitle="Nuevo cobro en mostrador"
            />
            <DashboardCard
              to="/app/pos/movimientos"
              icon={Clock}
              title="Movimientos"
              subtitle="Ingresos y egresos manuales"
            />
            <DashboardCard
              to="/app/pos/reporte"
              icon={FileText}
              title="Reporte de caja"
              subtitle="Arqueo y cierre"
            />
          </div>
        </div>
      )}
    </ClinicalPageShell>
    </div>
  );
}
